import { GitCompareArrows, Check, Star } from 'lucide-react'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import InlineFeedback from './InlineFeedback'
import { DataRow } from '@tendbot/ui'
import type { Comparison } from '../../../shared/types/chat'
import type { FeedbackType } from '../types'

interface Props {
  comparison: Comparison
  selectedOption?: string
  onSelect?: (id: string, name: string) => void
}

/**
 * Side-by-side comparison card -- AI presents options for the user to compare.
 * Selecting an option sends a message in chat.
 */
export default function ComparisonCard({ comparison, selectedOption, onSelect }: Props) {
  const { t } = useTranslation('chat')
  const { t: tc } = useTranslation('common')
  const hasSelection = !!selectedOption

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="glass-card rounded-xl overflow-hidden border-l-4 border-gold bg-gold/5"
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <GitCompareArrows size={14} className="text-gold" />
          <span className="text-xs font-semibold uppercase tracking-wider text-gold">
            {comparison.label || t('comparison.defaultLabel')}
          </span>
        </div>

        <h4 className="text-ivory font-medium text-sm mb-1">{comparison.title}</h4>
        {comparison.subtitle && (
          <p className="text-pearl/50 text-xs mb-3">{comparison.subtitle}</p>
        )}

        {/* Options */}
        <div className="space-y-2">
          {comparison.options.map((option) => {
            const isSelected = selectedOption === option.id
            const isDisabled = hasSelection && !isSelected

            return (
              <motion.button
                key={option.id}
                whileTap={!hasSelection ? { scale: 0.98 } : {}}
                onClick={() => !hasSelection && onSelect?.(option.id, option.name)}
                disabled={isDisabled}
                className={`w-full text-left p-3 rounded-xl border transition-all duration-200 ${
                  isSelected
                    ? 'border-gold bg-gold/10'
                    : isDisabled
                      ? 'border-white/5 bg-white/[0.01] opacity-40'
                      : 'border-white/10 bg-charcoal/30 hover:border-gold/30 hover:bg-gold/5'
                }`}
              >
                {/* Option header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-ivory text-sm font-medium">{option.name}</span>
                    {option.recommended && (
                      <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-gold/15 text-gold text-[9px] font-semibold uppercase tracking-wider">
                        <Star size={8} className="fill-gold" /> {t('comparison.pick')}
                      </span>
                    )}
                    {option.badge && (
                      <span className="px-1.5 py-0.5 rounded-full bg-white/5 text-pearl/50 text-[9px]">
                        {option.badge}
                      </span>
                    )}
                  </div>
                  {isSelected && <Check size={14} className="text-gold" />}
                </div>

                {/* Attributes */}
                <div className="space-y-1">
                  {option.attributes.map((attr, i) => (
                    <DataRow key={i} label={attr.label} value={attr.value} valueClassName={attr.highlight ? 'text-gold font-medium' : 'text-pearl/70'} />
                  ))}
                </div>

                {/* Price */}
                {option.price && (
                  <div className="flex justify-between items-baseline mt-2 pt-2 border-t border-white/5">
                    <span className="text-pearl/50 text-xs">
                      {option.priceDetail || t('comparison.price')}
                    </span>
                    <span className="text-gold font-semibold text-sm">{option.price}</span>
                  </div>
                )}
              </motion.button>
            )
          })}
        </div>

        {/* Note */}
        {comparison.note && (
          <div className="p-2.5 rounded-lg bg-gold/5 border border-gold/10 mt-3">
            <p className="text-xs text-pearl/70 italic">"{comparison.note}"</p>
          </div>
        )}

        {/* Feedback */}
        {hasSelection && (
          <div className="flex items-center justify-end gap-1 mt-3 pt-2 border-t border-white/5">
            <span className="text-xs text-pearl/50 mr-1">{tc('feedback.goodComparison')}</span>
            <InlineFeedback
              id={`comp-${comparison.id}`}
              onFeedback={(id: string, fb: FeedbackType) => console.log('Comparison feedback:', id, fb)}
            />
          </div>
        )}
      </div>
    </motion.div>
  )
}
